import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/assets/icons/LibrarySvg';
import Tooltip from '@/components/Common/Tooltip';
const CONTAINER_STYLE = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 10,
    background: '#21252755',
    minWidth: 130,
};
const NAME_TEXT_STYLE = {
    width: 130,
    flex: 1,
    color: '#818588',
    fontSize: '0.9rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};
const INPUT_STYLE = {
    width: 130,
    flex: 1,
    padding: 0,
    border: 'none',
    borderRadius: 0,
    backgroundColor: 'transparent',
    color: '#818588',
    fontSize: '0.9rem',
    outline: 'none',
};
const BUTTON_STYLE = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px 4px',
    border: 'none',
    borderRadius: 4,
    backgroundColor: 'transparent',
    cursor: 'pointer',
};
const ICON_CONTAINER_STYLE = {
    width: 14,
    height: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};
const MULTIPLE_ELEMENTS_STYLE = {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 10px',
    borderRadius: 10,
    background: '#21252799',
    color: '#818588',
    fontSize: '0.9rem',
};
// Хранилище последних сохраненных имен по ID элемента (сохраняется между размонтированиями)
const savedNamesCache = new Map();
/**
 * Компонент для редактирования имени элемента.
 * Показывает имя как текст, при клике на карандаш открывается инпут для редактирования.
 */
export const ElementNameEditor = ({ element, multipleElements, onUpdateName, }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState((element === null || element === void 0 ? void 0 : element.name) || '');
    const inputRef = useRef(null);
    const previousElementIdRef = useRef(null);
    const pendingSaveRef = useRef(null);
    // Синхронизация с элементом при смене или изменении имени
    useEffect(() => {
        if (!element) {
            previousElementIdRef.current = null;
            pendingSaveRef.current = null;
            return;
        }
        const currentElementId = element.id;
        const previousElementId = previousElementIdRef.current;
        const currentElementName = element.name || '';
        const cachedName = savedNamesCache.get(currentElementId);
        // Смена элемента - используем кэш или текущее имя
        if (currentElementId !== previousElementId) {
            previousElementIdRef.current = currentElementId;
            setIsEditing(false);
            pendingSaveRef.current = null;
            setEditedName(cachedName || currentElementName);
            return;
        }
        // Изменение имени с сервера (только если не редактируем и имя изменилось)
        if (isEditing || currentElementName === editedName)
            return;
        // Сервер вернул сохраненное имя - синхронизируем и очищаем кэш
        if (pendingSaveRef.current === currentElementName) {
            setEditedName(currentElementName);
            pendingSaveRef.current = null;
            savedNamesCache.delete(currentElementId);
            return;
        }
        // Есть кэш и он отличается - используем кэш (сервер еще не обновил)
        if (cachedName && cachedName !== currentElementName) {
            setEditedName(cachedName);
            return;
        }
        // Синхронизируем с сервером
        setEditedName(currentElementName);
        if (cachedName === currentElementName) {
            savedNamesCache.delete(currentElementId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [element === null || element === void 0 ? void 0 : element.id, element === null || element === void 0 ? void 0 : element.name, isEditing, editedName]);
    // Фокусируем инпут при начале редактирования
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);
    const handleStartEdit = (e) => {
        e.stopPropagation();
        if (multipleElements || !element)
            return;
        setIsEditing(true);
        setEditedName(element.name || '');
    };
    const handleSave = (e) => {
        e.stopPropagation();
        if (!element || !onUpdateName)
            return;
        const trimmedName = editedName.trim();
        setIsEditing(false);
        if (trimmedName !== element.name) {
            onUpdateName(element.id, trimmedName);
            setEditedName(trimmedName);
            pendingSaveRef.current = trimmedName;
            savedNamesCache.set(element.id, trimmedName);
        }
        else {
            setEditedName(element.name || '');
            pendingSaveRef.current = null;
            savedNamesCache.delete(element.id);
        }
    };
    const handleCancel = () => {
        if (element) {
            setEditedName(element.name || '');
        }
        setIsEditing(false);
        pendingSaveRef.current = null;
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave(e);
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        }
    };
    if (multipleElements) {
        return (_jsx("div", { style: MULTIPLE_ELEMENTS_STYLE, children: _jsx("span", { children: "multiple elements" }) }));
    }
    return (_jsx("div", { style: CONTAINER_STYLE, children: isEditing ? (_jsxs(_Fragment, { children: [_jsx("input", { ref: inputRef, type: "text", value: editedName, onChange: (e) => setEditedName(e.target.value), onKeyDown: handleKeyDown, onBlur: handleCancel, onClick: (e) => e.stopPropagation(), style: INPUT_STYLE }), _jsx(Tooltip, { text: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", position: "top", children: _jsx("button", { onClick: handleSave, onMouseDown: (e) => e.preventDefault(), style: Object.assign(Object.assign({}, BUTTON_STYLE), { color: '#4CAF50' }), "aria-label": "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", children: _jsx("div", { style: ICON_CONTAINER_STYLE, children: _jsx(Icon, { name: "check" }) }) }) })] })) : (_jsxs(_Fragment, { children: [_jsx("span", { style: NAME_TEXT_STYLE, children: editedName || 'empty name' }), _jsx(Tooltip, { text: "\u0420\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", position: "top", children: _jsx("button", { onClick: handleStartEdit, style: Object.assign(Object.assign({}, BUTTON_STYLE), { color: '#fff', opacity: 0.7 }), onMouseEnter: (e) => {
                            e.currentTarget.style.opacity = '1';
                        }, onMouseLeave: (e) => {
                            e.currentTarget.style.opacity = '0.7';
                        }, "aria-label": "\u0420\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", children: _jsx("div", { style: ICON_CONTAINER_STYLE, children: _jsx(Icon, { name: "pencil" }) }) }) })] })) }));
};
